import moment from 'moment/moment';
import type { ReactElement } from 'react';

import { DATE_RELATIONS, type DateRelation } from '@frontend/api';
import { useDateFormat } from '@frontend/utils';

import {
  renderAmountAndUnitInput,
  renderDateRangeInput,
  renderRelativeOrSingleDatePicker,
  renderSingleDateInput,
  renderUnitOnlyInput,
} from './DateRelationFilter.util';
import type {
  ExcludableDateRelation,
  RelationFilterWorkingValue,
} from './types';

type MainDateAndTimeInputsProps<
  ExcludedRelations extends ExcludableDateRelation,
> = {
  relationFilterValue: RelationFilterWorkingValue<ExcludedRelations>;
  onChange: (value: RelationFilterWorkingValue<ExcludedRelations>) => void;
  useNewBeforeAndAfter: boolean;
  blockFutureSelections: boolean;
  isPopoverOverlayEnabled?: boolean;
};

const MainDateAndTimeInputs = <
  ExcludedRelations extends ExcludableDateRelation,
>({
  relationFilterValue,
  onChange,
  useNewBeforeAndAfter = false,
  blockFutureSelections = false,
  isPopoverOverlayEnabled = false,
}: MainDateAndTimeInputsProps<ExcludedRelations>) => {
  const { dateFormat, dateTimeFormat } = useDateFormat();
  const dateRelationToRenderFunctionMap: Record<
    DateRelation,
    (
      relationFilterValue: RelationFilterWorkingValue<ExcludedRelations>,
      relationFilterOnChange: (
        value: RelationFilterWorkingValue<ExcludedRelations>,
      ) => void,
      isPopoverOverlayEnabled?: boolean,
    ) => ReactElement | null
  > = {
    [DATE_RELATIONS.PREVIOUS]: renderAmountAndUnitInput,
    [DATE_RELATIONS.NEXT]: renderAmountAndUnitInput,
    [DATE_RELATIONS.IN_THE_LAST]: renderAmountAndUnitInput,
    [DATE_RELATIONS.IN_THE_NEXT]: renderAmountAndUnitInput,
    [DATE_RELATIONS.CURRENT]: renderUnitOnlyInput,
    [DATE_RELATIONS.IN_THIS_HOUR]: () => null,
    [DATE_RELATIONS.TODAY]: () => null,
    [DATE_RELATIONS.IN_THIS_WEEK]: () => null,
    [DATE_RELATIONS.IN_THIS_MONTH]: () => null,
    [DATE_RELATIONS.IN_THIS_YEAR]: () => null,
    [DATE_RELATIONS.NEVER]: () => null,
    [DATE_RELATIONS.BEFORE]: (
      relationFilterValue,
      relationFilterOnChange,
      isPopoverOverlayEnabled,
    ) =>
      useNewBeforeAndAfter
        ? renderRelativeOrSingleDatePicker(
            blockFutureSelections,
            relationFilterValue,
            relationFilterOnChange,
            dateTimeFormat,
            {
              defaultValue: moment().startOf('day'),
              format: 'hh:mm',
              use12Hours: true,
            },
            isPopoverOverlayEnabled,
          )
        : renderSingleDateInput(
            relationFilterValue,
            relationFilterOnChange,
            dateTimeFormat,
            {
              defaultValue: moment().startOf('day'),
              format: 'hh:mm',
              use12Hours: true,
            },
          ),
    [DATE_RELATIONS.AFTER]: (
      relationFilterValue,
      relationFilterOnChange,
      isPopoverOverlayEnabled,
    ) =>
      useNewBeforeAndAfter
        ? renderRelativeOrSingleDatePicker(
            blockFutureSelections,
            relationFilterValue,
            relationFilterOnChange,
            dateTimeFormat,
            {
              defaultValue: moment().endOf('day'),
              format: 'hh:mm',
              use12Hours: true,
            },
            isPopoverOverlayEnabled,
          )
        : renderSingleDateInput(
            relationFilterValue,
            relationFilterOnChange,
            dateTimeFormat,
            {
              defaultValue: moment().endOf('day'),
              format: 'hh:mm',
              use12Hours: true,
            },
          ),
    [DATE_RELATIONS.ON]: (relationFilterValue, relationFilterOnChange) =>
      renderSingleDateInput(
        relationFilterValue,
        relationFilterOnChange,
        dateFormat,
        false,
      ),
    [DATE_RELATIONS.BETWEEN]: renderDateRangeInput,
  };

  const renderFunction =
    dateRelationToRenderFunctionMap[relationFilterValue.relation];

  const content = renderFunction(
    relationFilterValue,
    onChange,
    isPopoverOverlayEnabled,
  );

  return content;
};

export default MainDateAndTimeInputs;
